'use client'

import { captureException } from '@sentry/nextjs'
import { createContext, useContext, useEffect, useState } from 'react'

import { checkErrorInstance } from 'utils/check-error-instance'
import getAllEntitiesSlugs from 'utils/get-all-entities-slugs'
import type { CompareCredit } from '../../../types'

const NavUpsellCardContext = createContext<null | CompareCredit.FormattedCard>(
  null,
)

export const NavUpsellCardProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [navUpsellCard, setNavUpsellCard] =
    useState<null | CompareCredit.FormattedCard>(null)

  useEffect(() => {
    const abortController = new AbortController()

    const fetchNavUpsellCard = async () => {
      try {
        const response = await fetch('/api/get-nav-upsell-card', {
          signal: abortController.signal,
        })

        if (response.ok) {
          const responseObject: unknown = await response.json()

          if (
            !!responseObject &&
            typeof responseObject === 'object' &&
            'navUpsellCard' in responseObject
          ) {
            const { navUpsellCard: fetchedNavUpsellCard } = responseObject

            if (
              !!fetchedNavUpsellCard &&
              typeof fetchedNavUpsellCard === 'object' &&
              'slug' in fetchedNavUpsellCard &&
              typeof fetchedNavUpsellCard.slug === 'string' &&
              fetchedNavUpsellCard.slug.length > 0
            ) {
              const entities = await getAllEntitiesSlugs([
                fetchedNavUpsellCard.slug,
              ])

              if (entities && !entities.error) {
                const { cards } = entities
                const [card] = cards
                setNavUpsellCard(card)
              }
            }
          }
        } else {
          const error = new Error('Response from nav upsell card fetch not OK')

          captureException(error, {
            contexts: {
              error: {
                statusCode: response.status,
                statusText: response.statusText,
                url: response.url,
              },
            },
          })

          throw error
        }
      } catch (originalError) {
        const error = checkErrorInstance(originalError)

        captureException(error)
      }
    }

    fetchNavUpsellCard()

    return () => {
      abortController.abort()
    }
  }, [])

  return (
    <NavUpsellCardContext.Provider value={navUpsellCard}>
      {children}
    </NavUpsellCardContext.Provider>
  )
}

export const useNavUpsellCard = () => useContext(NavUpsellCardContext)
